import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { CreateTagRequest, UpdateTagRequest, VoterTagDto } from '@signalvote/shared';
import { authService } from '../../services/authService';
import { apiJson, apiVoid } from './apiFetch';
import { queryKeys } from '../../lib/queryKeys';
import { useCampaignStore } from '../../stores';

export interface UpdateTagData {
  id: number;
  updates: UpdateTagRequest;
}

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Fetch all tags
 */
export function useTags() {
  const { selectedCampaign } = useCampaignStore();

  return useQuery({
    queryKey: queryKeys.tags.list(),
    queryFn: () => apiJson<VoterTagDto[]>('/api/votertags', { error: 'Failed to fetch tags' }),
    enabled: authService.isAuthenticated() && !!selectedCampaign,
  });
}

// ============================================================================
// Mutation Hooks
// ============================================================================

/**
 * Create a new tag
 */
export function useCreateTag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateTagRequest) =>
      apiJson<VoterTagDto>('/api/votertags', {
        method: 'POST',
        json: data,
        error: (body) => new Error(body.error || 'Failed to create tag'),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tags.all });
    },
  });
}

/**
 * Update an existing tag
 */
export function useUpdateTag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, updates }: UpdateTagData) =>
      apiVoid(`/api/votertags/${id}`, {
        method: 'PUT',
        json: updates,
        error: (body) => new Error(body.error || 'Failed to update tag'),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tags.all });
    },
  });
}

/**
 * Delete a tag
 */
export function useDeleteTag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) =>
      apiVoid(`/api/votertags/${id}`, {
        method: 'DELETE',
        error: (body) => new Error(body.error || 'Failed to delete tag'),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tags.all });
    },
  });
}
